<template>
  <div class="wrapper">
    <v-layout v-if="showStatistics" column>
      <v-flex class="title-screen" xs12>
        <span>Ticket statistics</span>
      </v-flex>
      <div class="select-inputs">
        <v-select
          v-if="showStatistics"
          v-model="ticketSelectModel"
          :items="ticketSelectData"
          item-text="name"
          item-value="id"
          label="Ticket types"
          box
        />
        <v-select
          v-if="showStatistics"
          v-model="ticketSellerSelectModel"
          :items="ticketSellerSelectData"
          item-text="name"
          item-value="id"
          label="Sales channels"
          box
        />
        <v-select
          v-if="showStatistics && promoCodesSelectData.length"
          v-model="promoCodesSelectModel"
          :items="promoCodesSelectData"
          item-text="name"
          item-value="id"
          label="Promo Codes"
          box
          multiple
          @input="handleAllPromoCodesSelection"
        >
          <template v-slot:selection="{ item }">
            <span
              v-if="
                (selectedPromoCodeIds.includes(null) && item.id === null) ||
                  (!selectedPromoCodeIds.includes(null) && item.id !== null)
              "
              class="selection-promo-code-span"
            >{{ item.name }}</span>
          </template>
        </v-select>
      </div>
      <div
        class="statistics-card-container"
        :class="{ 'statistic-card-container__sport-organization': isSportOrganization() }"
      >
        <div v-if="annualTicketsSold !== null" class="statistics-card">
          <div class="statistics-card-content">
            <span class="statistic-card-title">Visitors number</span>
            <div class="total-sold-data">
              <span class="primary-data-style">{{ totalTicketsSold + annualTicketsSold }}</span>
            </div>
          </div>
          <img
            src="/assets/pie-chart.svg"
            alt="Pie chart icon"
            style="width: 1.5rem; height: 1.5rem"
          >
        </div>

        <div class="statistics-card" @click="handleTotalTicketsSoldClick">
          <div class="statistics-card-content">
            <span class="statistic-card-title">Total tickets sold</span>
            <div class="total-sold-data-wrapper">
              <div class="total-sold-data">
                <span class="primary-data-style">{{ totalTicketsSold }}</span>
                <span class="secondary-small-data-style">/ {{ totalTicketCount }}</span>
              </div>
              <span class="secondary-extra-small-data-style">
                {{ totalGratisTickets }} free ·
                {{ totalTicketsSold - totalGratisTickets }} paid</span>
            </div>
          </div>
          <img
            src="/assets/pie-chart.svg"
            alt="Pie chart icon"
            style="width: 1.5rem; height: 1.5rem"
          >
        </div>

        <div v-if="annualTicketsSold !== null" class="statistics-card">
          <div class="statistics-card-content">
            <span class="statistic-card-title">Annual tickets</span>
            <div class="total-sold-data">
              <span class="primary-data-style">{{ annualTicketsSold }}</span>
            </div>
          </div>
          <img
            src="/assets/pie-chart.svg"
            alt="Pie chart icon"
            style="width: 1.5rem; height: 1.5rem"
          >
        </div>

        <div
          class="statistics-card"
          @click="showSoldTicketsWithPromoCodeModal = !showSoldTicketsWithPromoCodeModal"
        >
          <div class="statistics-card-content">
            <span class="statistic-card-title">Used promocodes</span>
            <div class="total-sold-data">
              <span class="primary-data-style">{{ totalTicketsWithPromoSold }}</span>
            </div>
          </div>
          <img
            src="/assets/pie-chart.svg"
            alt="Pie chart icon"
            style="width: 1.5rem; height: 1.5rem"
          >
        </div>

        <div class="statistics-card" @click="handleTotalEarningsClick">
          <div class="statistics-card-content">
            <span class="statistic-card-title">Total earnings</span>
            <div class="total-earning-data">
              <span class="primary-data-style">{{ totalEarnings }} EUR</span>
            </div>
          </div>
          <img
            src="/assets/pie-chart.svg"
            alt="Pie chart icon"
            style="width: 1.5rem; height: 1.5rem"
          >
        </div>

        <div class="statistics-card" @click="handleCancelledTicketsClick">
          <div class="statistics-card-content">
            <span class="statistic-card-title">Cancelled Tickets</span>
            <span class="tertiary-data-style">{{ totalRefundedTicketsCount }}</span>
          </div>
          <img src="/assets/graph.svg" alt="Pie chart icon" style="width: 1.5rem; height: 1.5rem">
        </div>
      </div>

      <div v-if="showStatistics" class="grid">
        <div class="grid full-width-row">
          <graph
            title="Tickets sold by day"
            :has-data="ticketsSoldByDayChartData.labels.length !== 0"
            :data-filter="true"
            :initial-date-range-option="1"
            :date-filter-options="[1, 2, 3, 4]"
            :max-date-range="21"
            :header-total-label="totalTicketsSoldInSelectedRange"
            @changeDateRange="changeTotalTicketsDateRange($event)"
          >
            <stacked-bar-chart
              v-if="totalTicketsSold !== 0"
              :data="ticketsSoldByDayChartData"
              :options="{
                stackedY: true,
                barThickness: 22,
                barPercentage: 100,
                barDistance: 25,
                onClick: handleBarClick,
                onHover: handleBarHover,
              }"
            />
            <div v-else class="noDataText">
              No data to display. Statistics will be available when someone buys a ticket.
            </div>
            <div v-if="ticketsSoldByDayChartData.labels.length === 0">
              No data to display for selected time period
            </div>
          </graph>
        </div>

        <div class="grid other-rows">
          <graph
            title="Sold and available tickets"
            :data-filter="false"
            :has-data="true"
            :value="``"
          >
            <pie-chart :data="soldAndAvailableChartData" :options="{ displayLegend: true }" />
          </graph>
          <graph
            title="Heatmap for average ticket purchases"
            :data-filter="false"
            :has-data="true"
            :value="``"
            style="grid-row: span 2"
          >
            <heatmap
              :x-labels="heatmapXLabels"
              :y-labels="heatmapYLabels"
              :data="heatmapData"
              :groups="heatmapGroups"
            />
          </graph>
          <graph title="Daily cumulative revenue" :data-filter="false" :has-data="true" :value="``">
            <line-chart
              v-if="totalTicketsSold !== 0 && cumulativeTicketsRevenueChartData.labels.length > 1"
              :data="cumulativeTicketsRevenueChartData"
              :options="{}"
            />
            <div v-else class="noDataText">
              <div v-if="cumulativeTicketsRevenueChartData.labels.length === 1">
                Revenue for
                <b>
                  {{ cumulativeTicketsRevenueChartData.labels[0] }}:
                  {{ cumulativeTicketsRevenueChartData.datasets[0].data[0] }} EUR </b>. <br>
                More data will be displayed tomorrow.
              </div>
              <div v-else>
                No data to display. Statistics will be available when someone buys a ticket.
              </div>
            </div>
          </graph>
        </div>
        <div class="grid full-width-row">
          <graph
            title="Earnings by day"
            :data-filter="true"
            :has-data="earningsChartData.labels.length !== 0"
            :value="``"
            :initial-date-range-option="1"
            :date-filter-options="[1, 2, 3, 4]"
            :max-date-range="21"
            :header-total-label="totalEarningsInSelectedRange"
            @changeDateRange="changeEarningsDateRange($event)"
          >
            <bar-chart v-if="totalTicketsSold !== 0" :data="earningsChartData" :options="{}" />
            <div v-else class="noDataText">
              No data to display. Statistics will be available when someone buys a ticket.
            </div>
            <div v-if="earningsChartData.labels.length === 0">
              No data to display for selected time period
            </div>
          </graph>
        </div>
      </div>
    </v-layout>
    <div v-else class="no-stats not-available">
      <h1>As you start selling tickets, your statistics will appear here.</h1>
    </div>
    <refunded-tickets-modal
      v-if="showRefundedTicketsModal"
      :event-id="eventId"
      :event-to="eventTo"
      :total-refunded-tickets="totalRefundedTicketsCount"
      :order-item-id="selectedOrderItemId === null ? null : Number(selectedOrderItemId)"
      :ticket-seller-id="selectedTicketSellerId === null ? null : Number(selectedTicketSellerId)"
      :promo-code-ids="selectedPromoCodeIds.filter(id => id !== null)"
      :sale-channel-online="saleChannelOnline"
      @closeModal="showRefundedTicketsModal = !showRefundedTicketsModal"
    />
    <sold-tickets-modal
      v-if="soldTicketsModalData"
      :data="soldTicketsModalData"
      :title="soldTicketsModalTitle"
      @closeModal="soldTicketsModalData = null"
    />
    <ticket-types-statistics-modal
      v-if="showTicketTypesStatisticsModal"
      :data="ticketTypesData"
      :initial-selected-date="selectedTicketTypesDate"
      @closeModal="showTicketTypesStatisticsModal = !showTicketTypesStatisticsModal"
    />
    <sold-tickets-with-promo-code-modal
      v-if="showSoldTicketsWithPromoCodeModal"
      :data="soldTicketsByPromoCodeData"
      :promo-codes="promoCodes"
      :total-sold="totalTicketsWithPromoSold"
      :total-count="totalTicketCountWithPromoCode"
      @closeModal="showSoldTicketsWithPromoCodeModal = !showSoldTicketsWithPromoCodeModal"
    />
  </div>
</template>

<script>
import { GET_PURCHASE_BY_EVENT_2 } from '@/graphql/purchase.graphql'
import Graph from '../../components/shared/graphs/graph.vue'
import PieChart from '../../components/shared/graphs/pie-chart.vue'
import BarChart from '../../components/shared/graphs/bar-chart.vue'
import LineChart from '../../components/shared/graphs/line-chart.vue'
import StackedBarChart from '../../components/shared/graphs/stacked-bar-chart.vue'
import moment from 'moment'
import { exchangeRate, heatmapColorsArray } from '@/utils'
import { isEqual, round, sum } from 'lodash'
import RefundedTicketsModal from '@/components/stats/refunded-tickets-modal.vue'
import SoldTicketsModal from '@/components/stats/sold-tickets-modal.vue'
import TicketTypesStatisticsModal from '@/components/stats/ticket-types-statistics-modal.vue'
import Heatmap from '@/components/shared/graphs/heatmap.vue'
import SoldTicketsWithPromoCodeModal from '@/components/stats/sold-tickets-with-promo-code-modal.vue'
import sumBy from 'lodash/sumBy'

export default {
  name: 'ticket-statistics-container',
  components: {
    SoldTicketsWithPromoCodeModal,
    Heatmap,
    TicketTypesStatisticsModal,
    SoldTicketsModal,
    RefundedTicketsModal,
    Graph,
    PieChart,
    BarChart,
    LineChart,
    StackedBarChart,
  },
  props: {
    eventId: String,
    eventTemplateId: String,
    user: Object,
  },
  data() {
    return {
      ticketsSoldByDayChartData: null,
      soldAndAvailableChartData: null,
      earningsChartData: null,
      exchangeRate: exchangeRate,
      cumulativeTicketsRevenueChartData: null,
      showStatistics: false,
      totalEarnings: 0,
      totalTicketsSold: 0,
      totalTicketCount: 0,
      totalGratisTickets: 0,
      totalRefundedTicketsCount: 0,
      ticketSelectData: [],
      promoCodes: [],
      saleChannelOnline: false,
      ticketSelectModel: {
        name: 'test',
        id: null,
      },
      selectedOrderItemId: null,
      ticketSellerSelectData: [],
      ticketSellerSelectModel: {
        name: 'test',
        id: null,
      },
      selectedTicketSellerId: null,
      endDate: moment(new Date()).format('YYYY-MM-DD'),
      startDate: moment(new Date()).subtract(6, 'd').format('YYYY-MM-DD'),
      earningsEndDate: moment(new Date()).format('YYYY-MM-DD'),
      earningsStartDate: moment(new Date()).subtract(6, 'd').format('YYYY-MM-DD'),
      promoCodesSelectData: [],
      selectedPromoCodeIds: [],
      promoCodesSelectModel: [],
      showRefundedTicketsModal: false,
      eventTo: null,
      soldTicketsModalData: null,
      soldTicketsModalTitle: '',
      totalTicketsWithPromoSold: 0,
      totalTicketCountWithPromoCode: 0,
      totalPromoCodeEarnings: 0,
      showTicketTypesStatisticsModal: false,
      ticketTypesData: null,
      selectedTicketTypesDate: null,
      heatmapData: [],
      heatmapXLabels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      heatmapYLabels: [
        '00:00 - 07:00',
        '08:00',
        '09:00',
        '10:00',
        '11:00',
        '12:00',
        '13:00',
        '14:00',
        '15:00',
        '16:00',
        '17:00',
        '18:00',
        '19:00',
        '20:00',
        '21:00',
        '22:00',
        '23:00',
      ],
      heatmapGroups: [],
      showSoldTicketsWithPromoCodeModal: false,
      soldTicketsByPromoCodeData: [],
      annualTicketsSold: null,
    }
  },
  computed: {
    totalTicketsSoldInSelectedRange() {
      const total = sumBy(this.ticketsSoldByDayChartData.datasets, dataset => {
        return sum(dataset.data)
      })

      return total.toString()
    },
    totalEarningsInSelectedRange() {
      const totalEarnings = sumBy(this.earningsChartData.datasets, dataset => {
        return sum(dataset.data)
      })

      return `${round(totalEarnings, 2)} €`
    },
  },
  watch: {
    ticketSelectModel: function (value) {
      this.selectedOrderItemId = value
    },
    ticketSellerSelectModel: function (value) {
      if (value === 'online') {
        this.saleChannelOnline = true
        this.selectedTicketSellerId = null
      } else {
        this.selectedTicketSellerId = value
        this.saleChannelOnline = false
      }
    },
    promoCodesSelectModel: function (value) {
      this.selectedPromoCodeIds = value
    },
  },
  methods: {
    changeTotalTicketsDateRange(dateRangeObject) {
      this.startDate = moment(dateRangeObject.dateRange[0]).format('YYYY-MM-DD')
      this.endDate = moment(dateRangeObject.dateRange[1]).format('YYYY-MM-DD')
      this.filterByDate()
    },

    changeEarningsDateRange(dateRangeObject) {
      this.earningsStartDate = moment(dateRangeObject.dateRange[0]).format('YYYY-MM-DD')
      this.earningsEndDate = moment(dateRangeObject.dateRange[1]).format('YYYY-MM-DD')
      this.filterByDate()
    },

    async filterByDate() {
      await this.$apollo.queries.getPurchasesByEvent2.refetch()
    },

    async handleAllPromoCodesSelection(selectedPromoCodes) {
      if (
        (selectedPromoCodes.includes(null) && !this.selectedPromoCodeIds.includes(null)) ||
        (!this.selectedPromoCodeIds.includes(null) &&
          isEqual(
            selectedPromoCodes.sort(),
            this.promoCodesSelectData
              .map(item => item.id)
              .filter(id => id !== null)
              .sort(),
          ))
      ) {
        this.promoCodesSelectModel = this.promoCodesSelectData.map(item => item.id)
      } else if (!selectedPromoCodes.includes(null) && this.selectedPromoCodeIds.includes(null)) {
        this.promoCodesSelectModel = []
      } else if (
        selectedPromoCodes.includes(null) &&
        this.selectedPromoCodeIds.includes(null) &&
        !isEqual(selectedPromoCodes.sort(), this.promoCodesSelectData.sort())
      ) {
        this.promoCodesSelectModel = this.promoCodesSelectModel.filter(item => item !== null)
      }

      await this.$apollo.queries.getPurchasesByEvent2.refetch()
    },
    handleTotalTicketsSoldClick() {
      this.soldTicketsModalTitle = 'Total tickets sold'
      this.soldTicketsModalData = {
        labels: ['Regular', 'Promo', 'Gratis'],
        datasets: [
          {
            label: 'Prodane ulaznice',
            data: [
              this.totalTicketsSold - this.totalTicketsWithPromoSold - this.totalGratisTickets,
              this.totalTicketsWithPromoSold,
              this.totalGratisTickets,
            ],
            backgroundColor: ['#0277BD', '#56A4EF', '#CEE4F2'],
            hoverBackgroundColor: ['#0277BD', '#56A4EF', '#CEE4F2'],
          },
        ],
      }
    },
    handleTotalEarningsClick() {
      this.soldTicketsModalTitle = 'Total earnings'
      this.soldTicketsModalData = {
        labels: ['Regular', 'Promo'],
        datasets: [
          {
            label: 'Zarada',
            tooltipLabels: [
              round(this.totalEarnings - this.totalPromoCodeEarnings, 2) + ' €',
              this.totalPromoCodeEarnings + ' €',
            ],
            data: [this.totalEarnings - this.totalPromoCodeEarnings, this.totalPromoCodeEarnings],
            backgroundColor: ['#0277BD', '#CEE4F2'],
            hoverBackgroundColor: ['#0277BD', '#CEE4F2'],
          },
        ],
      }
    },
    handleCancelledTicketsClick() {
      this.showRefundedTicketsModal = !this.showRefundedTicketsModal
    },
    handleBarClick(event, elements) {
      if (elements[0]) {
        const i = elements[0].index

        this.selectedTicketTypesDate = this.ticketTypesData[i].date

        this.showTicketTypesStatisticsModal = true
      }
    },
    handleBarHover(event, chartElement) {
      if (chartElement.length) {
        event.native.target.style.cursor = 'pointer'
      } else {
        event.native.target.style.cursor = 'default'
      }
    },
    getTotalFloorPlanTicketQuantity(orderItems) {
      const parterTickets = orderItems.filter(oi => {
        if (this.selectedOrderItemId) {
          return oi.itemMeta.parter && oi.id === this.selectedOrderItemId
        } else {
          return oi.itemMeta.parter
        }
      })
      const parterTicketQuantity = parterTickets.reduce(
        (acc, curr) => (acc += curr.initialQuantity),
        0,
      )
      const tableTickets = orderItems.filter(oi => {
        if (this.selectedOrderItemId) {
          return oi.itemMeta.table && oi.id === this.selectedOrderItemId
        } else {
          return oi.itemMeta.table
        }
      })

      let tableTicketQuantity = 0
      const tableSeatsMap = new Map()
      for (const oi of tableTickets) {
        for (const seat of oi.sectionSeats) {
          if (!tableSeatsMap.has(seat)) {
            tableSeatsMap.set(seat, [oi.itemMeta.max])
          } else {
            const seats = tableSeatsMap.get(seat)
            if (!seats.includes(oi.itemMeta.max)) {
              seats.push(oi.itemMeta.max)
            }
          }
        }
      }
      for (const maxSeats of tableSeatsMap.values()) {
        tableTicketQuantity += maxSeats.reduce((acc, curr) => acc + curr, 0)
      }

      const seatTickets = orderItems.filter(oi => {
        if (this.selectedOrderItemId) {
          return !oi.itemMeta.table && !oi.itemMeta.parter && oi.id === this.selectedOrderItemId
        } else {
          return !oi.itemMeta.table && !oi.itemMeta.parter
        }
      })
      const seatTicketQuantity = [...new Set(seatTickets.map(oi => oi.sectionSeats).flat())].length

      return seatTicketQuantity + parterTicketQuantity + tableTicketQuantity
    },
    transformHeatmapData(data) {
      this.heatmapData = []
      for (let i = 0; i < 7; ++i) {
        const dataInOneDay = data.filter(item => item.e_dow === i)

        let ticketCountUntilSeven = 0
        for (const dataItem of dataInOneDay) {
          if (dataItem.e_hour <= 7) {
            ticketCountUntilSeven += dataItem.numberOfSoldTickets
          } else {
            this.heatmapData.push({
              x: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][i],
              y: `${String(dataItem.e_hour).padStart(2, '0')}:00`,
              value: dataItem.numberOfSoldTickets,
            })
          }
        }
        if (ticketCountUntilSeven > 0) {
          this.heatmapData.push({
            x: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][i],
            y: '00:00 - 07:00',
            value: ticketCountUntilSeven,
          })
        }
      }

      this.heatmapGroups = []
      const maxHeatmapValue = Math.max(...this.heatmapData.map(item => item.value))

      if (maxHeatmapValue <= 4) {
        for (let i = 0; i <= maxHeatmapValue; ++i) {
          this.heatmapGroups.push({
            lowerBound: i,
            upperBound: i,
            backgroundColor: heatmapColorsArray[i],
          })
        }
      } else {
        this.heatmapGroups.push({
          lowerBound: 0,
          upperBound: parseInt(maxHeatmapValue / 5, 10),
          backgroundColor: heatmapColorsArray[0],
        })
        for (let i = 1; i < 4; ++i) {
          this.heatmapGroups.push({
            lowerBound: Math.floor((i * maxHeatmapValue) / 5) + 1,
            upperBound: Math.floor(((i + 1) * maxHeatmapValue) / 5),
            backgroundColor: heatmapColorsArray[i],
          })
        }
        this.heatmapGroups.push({
          lowerBound: Math.floor((4 * maxHeatmapValue) / 5) + 1,
          upperBound: null,
          backgroundColor: heatmapColorsArray[4],
        })
      }
    },
    isSportOrganization() {
      return this.$route.path.includes('/sport-organizations/')
    },
  },

  apollo: {
    getPurchasesByEvent2: {
      query: GET_PURCHASE_BY_EVENT_2,
      variables() {
        return {
          eventId: this.eventId,
          orderItemId: this.selectedOrderItemId,
          ticketSellerId: this.selectedTicketSellerId,
          promoCodeIds: this.selectedPromoCodeIds.filter(id => id !== null),
          saleChannelOnline: this.saleChannelOnline,
          ticketsSoldFrom: this.startDate,
          ticketsSoldTo: this.endDate,
          earningsFrom: this.earningsStartDate,
          earningsTo: this.earningsEndDate,
        }
      },
      manual: true,
      result({ data, loading }) {
        if (!loading && !data) {
          return null
        }
        if (!loading && data.getPurchasesByEvent2) {
          const { getPurchasesByEvent2: statistics } = data
          const {
            eventTo,
            totalTicketsSold,
            totalTicketsSoldWithPromoCode,
            totalGratisTickets,
            totalTicketsAvailable,
            totalTicketsRefunded,
            ticketsSoldPerDay,
            ticketsSoldPerPromoCode,
            totalEarnings,
            totalPromoCodeEarnings,
            ticketsRevenuePerDay,
            cumulativeTicketsRevenue,
            orderItems,
            ticketSellers,
            promoCodes,
            soldTicketsGroupedByDayAndHour,
            annualTicketsSold,
          } = statistics

          this.eventTo = eventTo

          const tooltipLabels = ticketsSoldPerDay.map(ticket => {
            return {
              label: `${moment(ticket.date).format('D.M')}|Regular: ${Number(
                ticket.totalSold - ticket.ticketsPromo - ticket.ticketsGratis,
              )}|With Promo: ${Number(ticket.ticketsPromo)}|Gratis: ${Number(
                ticket.ticketsGratis,
              )}||IN TOTAL: ${
                Number(ticket.totalSold - ticket.ticketsPromo) + Number(ticket.ticketsPromo)
              }`,
            }
          })
          this.promoCodes = promoCodes
          this.ticketTypesData = ticketsSoldPerDay
          this.ticketsSoldByDayChartData = {
            labels: ticketsSoldPerDay.map(entry => moment(entry.date).format('D/M')),
            datasets: [
              {
                data: ticketsSoldPerDay.map(entry => entry.totalSold - entry.ticketsPromo - entry.ticketsGratis),
                tooltipLabels: tooltipLabels,
                bgColor: '#CEE4F2',
                borderColor: '#CEE4F2',
              },
              {
                data: ticketsSoldPerDay.map(entry => entry.ticketsGratis),
                tooltipLabels: tooltipLabels,
                bgColor: '#56A4EF',
                borderColor: '#56A4EF',
              },
              {
                data: ticketsSoldPerDay.map(entry => entry.ticketsPromo),
                tooltipLabels: tooltipLabels,
                bgColor: '#0277BD',
                borderColor: '#0277BD',
              },
            ],
          }
          this.ticketSelectData = [{ id: null, name: 'All ticket types' }, ...orderItems]
          this.ticketSellerSelectData = [
            { id: null, name: 'All sales channels' },
            { id: 'online', name: 'Online' },
            ...ticketSellers.map(s => {
              return { name: s.name, id: s.id }
            }),
          ]
          if (!this.promoCodesSelectData.length) {
            this.selectedPromoCodeIds = [null, ...promoCodes.map(item => item.id)]
            this.promoCodesSelectModel = [null, ...promoCodes.map(item => item.id)]
          }
          if (promoCodes.length) {
            this.promoCodesSelectData = [{ id: null, name: 'All promo codes' }, ...promoCodes]
          }

          this.totalTicketsSold = totalTicketsSold
          this.totalGratisTickets = totalGratisTickets
          this.totalTicketsWithPromoSold = totalTicketsSoldWithPromoCode
          if (
            data.getPurchasesByEvent2.orderItems.some(
              oi => oi.sectionSeats && oi.sectionSeats.length,
            )
          ) {
            this.totalTicketCount = this.getTotalFloorPlanTicketQuantity(
              data.getPurchasesByEvent2.orderItems,
            )
          } else {
            this.totalTicketCount = this.selectedOrderItemId
              ? orderItems.find(oi => oi.id == this.selectedOrderItemId).initialQuantity
              : this.totalTicketsSold + totalTicketsAvailable
          }

          this.totalEarnings = totalEarnings
          this.totalPromoCodeEarnings = totalPromoCodeEarnings

          this.totalTicketCountWithPromoCode = ticketsSoldPerPromoCode.some(item => item.unlimited)
            ? 0
            : ticketsSoldPerPromoCode.reduce((acc, curr) => (acc += curr.initialQuantity), 0)

          this.soldTicketsByPromoCodeData = ticketsSoldPerPromoCode
          this.totalRefundedTicketsCount = totalTicketsRefunded

          this.earningsChartData = {
            labels: ticketsRevenuePerDay.map(entry => moment(entry.date).format('D/M')),
            datasets: [
              {
                label: 'Revenue EUR',
                tooltipLabels: ticketsRevenuePerDay.map(
                  r => `${moment(r.date).format('D.M')}|${r.revenue} EUR`,
                ),
                data: ticketsRevenuePerDay.map(entry => entry.revenue),
                backgroundColor: '#CEE4F2',
                hoverBackgroundColor: '#CEE4F2',
              },
            ],
          }

          this.soldAndAvailableChartData = {
            labels: ['Sold tickets', 'Available tickets'],
            datasets: [
              {
                label: 'Zarada',
                data: [this.totalTicketsSold, this.totalTicketCount - this.totalTicketsSold],
                backgroundColor: ['#0277BD', '#CEE4F2'],
                hoverBackgroundColor: ['#0277BD', '#CEE4F2'],
              },
            ],
          }

          if (this.isSportOrganization()) {
            this.annualTicketsSold = annualTicketsSold
            this.soldAndAvailableChartData.labels.push('Annual tickets')
            this.soldAndAvailableChartData.datasets[0].data.push(annualTicketsSold)
            this.soldAndAvailableChartData.datasets[0].backgroundColor.push('#56A4EF')
            this.soldAndAvailableChartData.datasets[0].hoverBackgroundColor.push('#56A4EF')
          }

          this.cumulativeTicketsRevenueChartData = {
            labels: cumulativeTicketsRevenue.map(entry => moment(entry.date).format('D/M')),
            datasets: [
              {
                title: 'Revenue EUR',
                tooltipLabels: cumulativeTicketsRevenue.map(
                  entry => `${moment(entry.date).format('D.M')}|${entry.revenue} EUR`,
                ),
                data: cumulativeTicketsRevenue.map(entry => entry.revenue.toFixed(2)),
              },
            ],
          }

          this.transformHeatmapData(soldTicketsGroupedByDayAndHour)

          this.showStatistics = true
        }
      },
    },
  },
}
</script>

<style scoped>
.not-available h1 {
  color: #979797;
  line-height: 24px;
  font-weight: 600;
  font-family: 'Raleway';
  font-size: 20px;
  text-align: center;
}
.grid {
  margin-bottom: 34px;
  display: grid;
  /* grid-template-columns: repeat(auto-fit, minmax(450px, 1fr)); */
  grid-gap: 26px;
}
.full-width-row {
  grid-template-columns: 1fr; /* The first row takes up the whole space */
}

.other-rows {
  grid-template-columns: repeat(
    auto-fit,
    minmax(450px, 1fr)
  ); /* Other rows use auto-fit and minmax */
}
.grid h3 {
  text-align: center;
}
.no-stats {
  height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
}
.statistics-card-container {
  display: flex;
  padding: 1rem;
  align-items: stretch;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 24px;
  flex-wrap: wrap;

  border-radius: 1rem;
  background: #f5f5f5;
}
.statistics-card {
  display: flex;
  padding: 1rem;
  gap: 1rem;
  justify-content: space-between;
  align-items: flex-end;
  flex: 1 0 0;

  border-radius: 0.5rem;
  border: 1px solid #fff;
  background: #fff;
}
.statistics-card:hover {
  cursor: pointer;

  border: 1px solid #d9d9d9;
  box-shadow: 0 4px 16px 0 rgba(51, 51, 51, 0.16);
}
.statistic-card-title {
  color: #666;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.0375rem;
  white-space: nowrap;
}
.statistics-card-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  height: 100%;
  justify-content: space-between;
}
.total-sold-data-wrapper {
  display: flex;
  flex-direction: column;
}
.total-sold-data {
  display: flex;
  align-items: baseline;
}
.primary-data-style {
  color: #0277bd;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 2.25rem;
  font-style: normal;
  font-weight: 800;
  line-height: 100%;
  letter-spacing: -0.0675rem;
  white-space: nowrap;
  @media screen and (max-width: 860px) {
    font-size: 1.75rem;
  }
}
.secondary-small-data-style {
  color: #666;
  font-variant-numeric: lining-nums proportional-nums;

  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.0375rem;
  white-space: nowrap;
  @media screen and (max-width: 860px) {
    font-size: 0.75rem;
  }
}
.secondary-extra-small-data-style {
  font-family: Source Sans Pro;
  font-weight: 400;
  font-size: 18px;
  line-height: 150%;
  letter-spacing: 0%;
  color: #666666;
}
.total-earning-data {
  display: flex;
  align-items: baseline;
  gap: 1rem;
}

.tertiary-data-style {
  color: #ff8875;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 2.25rem;
  font-style: normal;
  font-weight: 800;
  line-height: 100%;
  letter-spacing: -0.0675rem;
  white-space: nowrap;
  @media screen and (max-width: 860px) {
    font-size: 1.75rem;
  }
}
@media (max-width: 800px) {
  .statistics-card-container {
    flex-direction: column;
  }
  .statistics-card {
    margin: 12px auto !important;
    width: 100% !important;
  }
}
@media (max-width: 500px) {
  .grid {
    grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  }
}
@media (max-width: 380px) {
  .grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
}
.wrapper {
  max-width: 1033px;
}
.noDataText {
  height: 175px;
  margin-top: 150px;
  color: grey;
}
.select-inputs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  grid-gap: 26px;
}
@media screen and (max-width: 500px) {
  .select-inputs {
    grid-gap: 12px;
  }
}
@media screen and (max-width: 800px) {
  .wrapper {
    margin-top: 1.5rem;
  }
}
.selection-promo-code-span:not(:first-child)::before {
  content: ', ';
}
.statistic-card-container__sport-organization .statistics-card {
  flex: 1 1 calc(33.33% - 1rem);
}
</style>
